import { routes } from '../routes/routes';

const fallbackSiteUrl = 'https://marlta.com';
const defaultImagePath = '/landing/marlta-hero-main.png';

export const seoConfig = {
  siteName: '말타',
  companyName: '(주)럭스포',
  locale: 'ko_KR',
  language: 'ko-KR',
  defaultImagePath,
  defaultKeywords: [
    '말타',
    '승마',
    '승마예약',
    '승마장검색',
    '승마체험',
    '승마장',
    '승마뉴스',
  ],
};

export const routeSeo = {
  [routes.home]: {
    title: '말타 | 승마 예약의 시작',
    description:
      '말타에서 가까운 승마장을 찾고 프로그램, 가격, 예약 가능 시간을 확인해 승마 예약을 쉽게 시작하세요.',
    keywords: ['승마예약', '승마체험', '승마장', '말타앱'],
    imagePath: defaultImagePath,
  },
  [routes.horseClub]: {
    title: '승마장검색 | 말타',
    description:
      '지역, 프로그램, 예약 가능 정보를 기준으로 나에게 맞는 승마장을 찾고 승마체험과 강습 정보를 확인하세요.',
    keywords: ['승마장검색', '내 주변 승마장', '승마체험', '승마강습'],
    imagePath: '/landing/marlta-coverage-map.png',
  },
  [routes.services]: {
    title: '서비스 | 말타',
    description:
      '승마 예약, 강습 기록, 승마장 운영 관리까지 말타가 연결하는 승마 서비스를 확인하세요.',
    keywords: ['말타 서비스', '승마 예약 서비스', '승마장 관리'],
    imagePath: defaultImagePath,
  },
  [routes.news]: {
    title: '승마뉴스 | 말타',
    description:
      '승마를 더 쉽게 시작할 수 있는 정보와 말타 서비스 소식, 승마장 파트너 뉴스를 확인하세요.',
    keywords: ['승마뉴스', '말타뉴스', '승마정보', '승마소식'],
    imagePath: '/landing/equestrian-horses.jpg',
  },
  [routes.community]: {
    title: '커뮤니티 | 말타',
    description:
      '승마를 시작하는 사람과 즐기는 사람이 경험, 질문, 소식을 함께 나누는 말타 커뮤니티입니다.',
    keywords: ['승마커뮤니티', '승마후기', '승마질문', '승마모임'],
    imagePath: '/landing/equestrian-arena.jpg',
  },
  [routes.partners]: {
    title: '파트너스 | 말타',
    description: '승마장 운영과 홍보, 신규회원 모집을 돕는 말타 파트너 서비스를 소개합니다.',
    keywords: ['말타 파트너스', '승마장 운영', '승마장 홍보'],
    imagePath: '/landing/marlta-partner-office.png',
  },
  [routes.support]: {
    title: '고객센터 | 말타',
    description: '말타 서비스 이용, 예약, 파트너 상담에 필요한 공식 문의 채널입니다.',
    keywords: ['말타 고객센터', '말타 문의', '파트너 상담'],
    imagePath: defaultImagePath,
  },
  [routes.myInfo]: {
    title: '내정보 | 말타',
    description: '말타 예약 현황, 강습 내역, 개인 알림을 확인하는 내정보 페이지입니다.',
    keywords: ['말타 내정보', '승마예약 내역', '강습 내역'],
    imagePath: defaultImagePath,
    robots: 'noindex,nofollow',
  },
  [routes.intro]: {
    title: '말타 앱 소개 | 승마 예약 플랫폼',
    description:
      '가까운 승마장 검색부터 프로그램 확인, 예약, 강습 기록까지 말타 앱에서 이어지는 승마 예약 플랫폼을 소개합니다.',
    keywords: ['말타 앱', '승마 예약 플랫폼', '승마장 앱', '승마체험 예약'],
    imagePath: defaultImagePath,
  },
  [routes.kraCitizenEvent]: {
    title: '말타 앱 업데이트 안내',
    description: '일반시민 대상 승마교실 신청을 위해 최신버전의 말타앱 설치를 안내합니다.',
    keywords: ['말타 앱 업데이트', '승마교실', '일반시민 승마교실'],
    imagePath: defaultImagePath,
    robots: 'noindex,nofollow',
  },
};

export function getSiteUrl() {
  const configuredUrl = process.env.REACT_APP_MARLTA_SITE_URL;

  if (configuredUrl) {
    return configuredUrl.replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/+$/, '');
  }

  return fallbackSiteUrl;
}

export function getRouteSeo(path) {
  return routeSeo[path] || routeSeo[routes.home];
}

export function getRouteUrl(path, siteUrl = getSiteUrl()) {
  if (path === routes.home) {
    return `${siteUrl}/`;
  }

  return `${siteUrl}${path}`;
}

export function getAbsoluteAssetUrl(assetPath, siteUrl = getSiteUrl()) {
  if (/^https?:\/\//i.test(assetPath)) {
    return assetPath;
  }

  const publicUrl = process.env.PUBLIC_URL || '';
  const normalizedAssetPath = assetPath.startsWith('/') ? assetPath : `/${assetPath}`;

  return `${siteUrl}${publicUrl}${normalizedAssetPath}`;
}

export function getKeywords(seo) {
  return [...new Set([...seoConfig.defaultKeywords, ...(seo.keywords || [])])].join(', ');
}

export function getJsonLd(path, seo = getRouteSeo(path)) {
  const siteUrl = getSiteUrl();
  const url = getRouteUrl(path, siteUrl);
  const imageUrl = getAbsoluteAssetUrl(seo.imagePath || seoConfig.defaultImagePath, siteUrl);
  const breadcrumbItems = [
    {
      '@type': 'ListItem',
      position: 1,
      name: '홈',
      item: getRouteUrl(routes.home, siteUrl),
    },
  ];

  if (path !== routes.home) {
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: 2,
      name: seo.title.replace(/\s*\|\s*말타.*/, ''),
      item: url,
    });
  }

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: seoConfig.companyName,
      alternateName: seoConfig.siteName,
      url: siteUrl,
      logo: getAbsoluteAssetUrl('/logo512.png', siteUrl),
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+82-507-1325-0543',
        contactType: 'customer support',
        email: 'help@luxspo.com',
        availableLanguage: ['ko'],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: seoConfig.siteName,
      url: siteUrl,
      inLanguage: seoConfig.language,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${getRouteUrl(routes.horseClub, siteUrl)}?keyword={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: seo.title,
      description: seo.description,
      url,
      inLanguage: seoConfig.language,
      isPartOf: {
        '@type': 'WebSite',
        name: seoConfig.siteName,
        url: siteUrl,
      },
      primaryImageOfPage: {
        '@type': 'ImageObject',
        url: imageUrl,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbItems,
    },
  ];
}
